'use client';

import { type ReactNode } from 'react';

import type { ImpressionContext } from '../../core/constants';
import type { ImpressionMetadata } from '../../core/types';
import { ImpressionArea } from '../../../react/ImpressionArea';
import { usePersonalizationTracker } from '../PersonalizationProvider';

/**
 * Props for TrackImpression component.
 *
 * @property {string} productId - Unique product identifier
 * @property {ImpressionContext} context - Context where impression occurs
 * @property {ImpressionMetadata} [metadata] - Optional impression metadata (position is typically required)
 * @property {number} [areaThreshold] - Percentage of area that must be visible (0-1, default: 0.5)
 * @property {number} [timeThreshold] - Milliseconds element must be visible (default: 1000ms)
 * @property {boolean} [impressionOnce] - Track impression only once (default: true)
 * @property {string} [className] - Optional CSS class name
 * @property {ReactNode} children - Child components to render
 */
interface TrackImpressionProps {
  productId: string;
  context: ImpressionContext;
  metadata?: ImpressionMetadata;
  areaThreshold?: number;
  timeThreshold?: number;
  impressionOnce?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Component that tracks product impressions using Intersection Observer.
 * Fires impression event when the element becomes visible in the viewport
 * for a specified duration and visibility threshold.
 *
 * @param {TrackImpressionProps} props - Component props
 *
 * @example
 * // Basic usage - tracks when 50% visible for 1 second
 * <TrackImpression
 *   productId="prod_123"
 *   context="search_results"
 *   metadata={{ position: 3 }}
 * >
 *   <ProductCard />
 * </TrackImpression>
 *
 * @example
 * // Custom thresholds
 * <TrackImpression
 *   productId="prod_123"
 *   context="recommendation_section"
 *   metadata={{ position: 1, sectionName: 'Popular Products' }}
 *   areaThreshold={0.8}
 *   timeThreshold={2000}
 *   impressionOnce={false}
 * >
 *   <ProductCard />
 * </TrackImpression>
 */
export function TrackImpression({
  productId,
  context,
  metadata,
  areaThreshold,
  timeThreshold,
  impressionOnce = true,
  className,
  children,
}: TrackImpressionProps) {
  const tracker = usePersonalizationTracker();

  return (
    <ImpressionArea
      areaThreshold={areaThreshold}
      timeThreshold={timeThreshold}
      impressionOnce={impressionOnce}
      className={className}
      onImpressionStart={() => {
        tracker.impression(productId, context, metadata);
      }}
    >
      {children}
    </ImpressionArea>
  );
}
